import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useEcommerceTracking } from '../hooks/useAnalytics';
import { useProducts, useCategories, useWishlist, useFilterOptions, usePrefetch, useAddToWishlist, useRemoveFromWishlist } from '../hooks/useApiCache';
import { preloadProductImages } from '../utils/imageCache';
import { usePerformance } from '../hooks/usePerformance';
import { 
  MagnifyingGlassIcon, 
  AdjustmentsHorizontalIcon,
  HeartIcon,
  EyeIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import AdvancedSearch from '../components/search/AdvancedSearch';
import MobileFilters from '../components/search/MobileFilters';
import QuickViewModal from '../components/ui/QuickViewModal';
import OptimizedImage from '../components/ui/OptimizedImage';
import { ProductSkeletonGrid } from '../components/ui/ProductSkeleton';
import { 
  FragranceFamilyFilter, 
  PerfumeEmptyState, 
  OccasionFilter, 
  IntensityFilter,
  PerfumeFinder
} from '../components/perfume';
import CollapsibleSection from '../components/ui/CollapsibleSection';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showPerfumeFinder, setShowPerfumeFinder] = useState(false);
  
  // Perfume-specific filters
  const [fragranceFamily, setFragranceFamily] = useState('');
  const [occasion, setOccasion] = useState('');
  const [intensity, setIntensity] = useState(null);
  
  const { user, isAuthenticated } = useAuth();
  const { trackSearch } = useEcommerceTracking();
  const { addToCart } = useCart();
  const { prefetchProduct } = usePrefetch();
  const { trackUserInteraction, measureComponentRender } = usePerformance();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    sort: 'created_at',
    order: 'desc',
    brand: '',
    minRating: '',
    inStock: false,
    tags: '',
    fragranceFamily: '',
    occasion: '',
    intensity: null
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Use React Query hooks
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: wishlistData, isLoading: wishlistLoading } = useWishlist();
  const { data: filterOptionsData } = useFilterOptions();
  const { 
    data: productsData, 
    isLoading: productsLoading, 
    error: productsError,
    isPlaceholderData 
  } = useProducts(filters, currentPage);

  // Wishlist mutations
  const addToWishlistMutation = useAddToWishlist();
  const removeFromWishlistMutation = useRemoveFromWishlist();

  // Extract data from React Query responses
  const categories = categoriesData || [];
  const wishlist = new Set(
    Array.isArray(wishlistData) 
      ? wishlistData.map(item => item.product_id) 
      : []
  );
  const products = productsData?.products || [];
  const pagination = productsData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  };
  
  const loading = categoriesLoading || productsLoading;
  const loadingProducts = productsLoading || isPlaceholderData;

  // Initialize filters from URL
  useEffect(() => {
    const urlFilters = {
      search: searchParams.get('search') || '',
      category: searchParams.get('category_id') || '',
      minPrice: searchParams.get('min_price') || '',
      maxPrice: searchParams.get('max_price') || '',
      sort: searchParams.get('sort') || 'created_at',
      order: searchParams.get('order') || 'desc',
      brand: searchParams.get('brand') || '',
      minRating: searchParams.get('min_rating') || '',
      inStock: searchParams.get('in_stock') === 'true',
      tags: searchParams.get('tags') || '',
      fragranceFamily: searchParams.get('fragrance_family') || '',
      occasion: searchParams.get('occasion') || '',
      intensity: searchParams.get('intensity') ? parseInt(searchParams.get('intensity')) : null
    };
    setFilters(urlFilters);
    setFragranceFamily(urlFilters.fragranceFamily);
    setOccasion(urlFilters.occasion);
    setIntensity(urlFilters.intensity);
  }, [searchParams]);

  // Update page when filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters]);

  // Preload product images when products are loaded
  useEffect(() => {
    if (products.length > 0) {
      const startTime = performance.now();
      preloadProductImages(products);
      measureComponentRender('ProductsPage-ImagePreload', startTime);
    }
  }, [products, measureComponentRender]);

  // Update filters and URL
  const updateFilters = useCallback((newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    
    // Track search if search term changed
    if (newFilters.search && newFilters.search !== filters.search && newFilters.search.trim()) {
      trackSearch(newFilters.search.trim());
    }
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== '' && value !== false && value !== null && value !== undefined) {
        const keyMap = {
          minPrice: 'min_price',
          maxPrice: 'max_price',
          minRating: 'min_rating',
          inStock: 'in_stock',
          fragranceFamily: 'fragrance_family'
        };
        const urlKey = keyMap[key] || key;
        params.set(urlKey, String(value));
      }
    });
    setSearchParams(params);
    
  }, [filters, setSearchParams, trackSearch]);

  // Clear all perfume filters
  const clearPerfumeFilters = () => {
    setFragranceFamily('');
    setOccasion('');
    setIntensity(null);
    updateFilters({ fragranceFamily: '', occasion: '', intensity: null });
  };

  // Handle perfume filter changes
  const handleFragranceFamilyChange = (family) => {
    setFragranceFamily(family);
    updateFilters({ fragranceFamily: family });
  };

  const handleOccasionChange = (occ) => {
    setOccasion(occ);
    updateFilters({ occasion: occ });
  };

  const handleIntensityChange = (level) => {
    setIntensity(level);
    updateFilters({ intensity: level });
  };

  const hasActiveFilters = filters.search || filters.category || filters.brand || 
                          filters.minPrice || filters.maxPrice || filters.minRating ||
                          fragranceFamily || occasion || intensity;

  // Wishlist toggle
  const toggleWishlist = async (productId) => {
    if (!isAuthenticated) {
      toast.error('יש להתחבר כדי להוסיף למשאלות');
      navigate('/login');
      return;
    }

    try {
      if (wishlist.has(productId)) {
        await removeFromWishlistMutation.mutateAsync(productId);
        toast.success('המוצר הוסר מרשימת המשאלות');
      } else {
        await addToWishlistMutation.mutateAsync(productId);
        toast.success('המוצר נוסף לרשימת המשאלות');
      }
    } catch (error) {
      toast.error('שגיאה בעדכון רשימת המשאלות');
    }
  };
  
  const openQuickView = (product, e) => {
    if (e) e.stopPropagation();
    setQuickViewProduct(product);
  };
  
  const closeQuickView = () => {
    setQuickViewProduct(null);
  };

  const getProductBadge = (product) => {
    if (product.is_featured) return { type: 'featured', text: 'מומלץ', color: 'bg-yellow-500' };
    if (new Date(product.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
      return { type: 'new', text: 'חדש', color: 'bg-green-500' };
    }
    if (product.stock_quantity < product.min_stock_level) {
      return { type: 'limited', text: 'מלאי מוגבל', color: 'bg-red-500' };
    }
    return null;
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getCurrentPrice = (product) => {
    if (user?.role === 'company_admin' || user?.role === 'company_user') {
      return product.price_b2b || product.price_b2c;
    }
    return product.price_b2c;
  };

  const renderStars = (rating, productId = '') => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={`${productId}-star-${i}`}
            className={`h-4 w-4 ${
              i < Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-First Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">מוצרים</h1>
              <p className="text-sm text-gray-600 mt-1">
                {pagination.totalItems} מוצרים
              </p>
            </div>
          </div>

          {/* Mobile Search Bar - Only show on mobile */}
          <div className="lg:hidden flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="חפש מוצרים..."
                value={filters.search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base bg-gray-50 focus:bg-white transition-colors"
                onKeyPress={(e) => e.key === 'Enter' && updateFilters(filters)}
              />
            </div>
            <button
              onClick={() => setShowMobileFilters(true)}
              className="flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Perfume Filters - Mobile Quick Filters */}
      <div className="lg:hidden px-4 py-4 space-y-4 bg-white border-b border-gray-200">
        <FragranceFamilyFilter
          selected={fragranceFamily}
          onChange={handleFragranceFamilyChange}
        />
        <OccasionFilter
          selected={occasion}
          onChange={handleOccasionChange}
        />
      </div>

      {/* Desktop Advanced Search Component */}
      <div className="hidden lg:block px-4 py-6">
        <AdvancedSearch
          filters={filters}
          onFiltersChange={updateFilters}
          onSearch={(searchFilters) => updateFilters(searchFilters)}
          categories={categories}
        />
      </div>
      
      {/* Mobile Sort Bar */}
      <div className="md:hidden px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {pagination.totalItems} תוצאות
          </span>
          <select
            value={`${filters.sort}-${filters.order}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              updateFilters({ sort, order });
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="created_at-desc">חדש ביותר</option>
            <option value="created_at-asc">ישן ביותר</option>
            <option value="name-asc">שם א-ת</option>
            <option value="name-desc">שם ת-א</option>
            <option value="price_b2c-asc">מחיר נמוך לגבוה</option>
            <option value="price_b2c-desc">מחיר גבוה לנמוך</option>
            <option value="rating_average-desc">דירוג גבוה</option>
            <option value="sales_count-desc">הנמכרים ביותר</option>
          </select>
        </div>
      </div>

      {/* Main Products Container with Sidebar */}
      <div className="px-4 pb-6">
        <div className="flex gap-8">
          {/* Desktop Perfume Filters Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-4">
              <CollapsibleSection title="🌺 משפחות ריח" defaultOpen={true}>
                <FragranceFamilyFilter
                  selected={fragranceFamily}
                  onChange={handleFragranceFamilyChange}
                />
              </CollapsibleSection>
              
              <CollapsibleSection title="🎯 אירועים" defaultOpen={true}>
                <OccasionFilter
                  selected={occasion}
                  onChange={handleOccasionChange}
                />
              </CollapsibleSection>
              
              <CollapsibleSection title="💪 עוצמת ניחוח" defaultOpen={false}>
                <IntensityFilter
                  selected={intensity}
                  onChange={handleIntensityChange}
                />
              </CollapsibleSection>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearPerfumeFilters}
                  className="w-full px-4 py-3 bg-gradient-to-r from-red-50 to-pink-50 text-red-700 rounded-lg hover:from-red-100 hover:to-pink-100 transition-all font-medium text-sm border border-red-200"
                >
                  🗑️ נקה כל הפילטרים
                </button>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <ProductSkeletonGrid count={12} />
            ) : products.length === 0 ? (
              <PerfumeEmptyState
                onStartFinder={() => setShowPerfumeFinder(true)}
                onClearFilters={clearPerfumeFilters}
                hasFilters={hasActiveFilters}
              />
            ) : (
          <>
            {/* Mobile-First Product Grid */}
            <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 ${loadingProducts ? 'opacity-50' : ''}`}>
                {products.map((product) => {
                  const badge = getProductBadge(product);
                  const isInWishlist = wishlist.has(product.id);
                  
                  return (
                    <div 
                      key={product.id} 
                      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      onMouseEnter={() => {
                        setHoveredProduct(product.id);
                        prefetchProduct(product.id); // Prefetch product data on hover
                      }}
                      onMouseLeave={() => setHoveredProduct(null)}
                    >
                      {/* Product Image Container */}
                      <div className="relative overflow-hidden">
                        <Link to={`/products/${product.id}`}>
                          <div className="aspect-square bg-gray-100">
                            <OptimizedImage
                              src={product.images?.[0] ? (product.images[0].startsWith('http') ? product.images[0] : product.images[0]) : null}
                              alt={product.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              lazy={true}
                              quality={80}
                            />
                          </div>
                        </Link>
                        
                        {/* Product Badge */}
                        {badge && (
                          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium text-white ${badge.color} shadow-lg`}>
                            {badge.text}
                          </div>
                        )}
                        
                        {/* Quick Action Buttons - Mobile & Desktop */}
                        <div className="absolute top-2 left-2 flex flex-col gap-2">
                          {/* Wishlist Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleWishlist(product.id);
                            }}
                            className={`p-2.5 sm:p-2 rounded-full shadow-lg transition-all duration-300 touch-manipulation ${
                              isInWishlist 
                                ? 'bg-red-500 text-white' 
                                : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-500 active:bg-red-100'
                            }`}
                            aria-label={isInWishlist ? 'הסר מהמשאלות' : 'הוסף למשאלות'}
                          >
                            <HeartIcon className={`h-5 w-5 sm:h-4 sm:w-4 ${isInWishlist ? 'fill-current' : ''}`} />
                          </button>
                          
                          {/* Quick View Button - Desktop Only */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openQuickView(product, e);
                            }}
                            className="hidden sm:block p-2 bg-white text-gray-600 rounded-full shadow-lg hover:bg-primary-50 hover:text-primary-600 active:bg-primary-100 transition-all duration-300"
                            aria-label="תצוגה מהירה"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="p-3 sm:p-4">
                        {/* Brand */}
                        {product.brand && (
                          <p className="text-xs sm:text-sm text-gray-500 mb-1">{product.brand}</p>
                        )}
                        
                        <Link to={`/products/${product.id}`} className="block">
                          <h3 className="font-semibold text-gray-900 mb-1.5 sm:mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors text-sm sm:text-base min-h-[2.5rem] sm:min-h-[3rem]">
                            {product.name}
                          </h3>
                        </Link>
                        
                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          {renderStars(product.rating_average || 0, product.id)}
                          <span className="text-xs text-gray-500">
                            ({product.rating_count || 0})
                          </span>
                        </div>

                        {/* Price */}
                        <div className="mb-2 sm:mb-3">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-primary-600 text-base sm:text-lg">
                              {formatPrice(getCurrentPrice(product))}
                            </span>
                            
                            {/* Stock Status - Responsive */}
                            <div className={`text-xs sm:text-sm font-medium ${
                              product.stock_quantity > 0 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              <span className="hidden sm:inline">
                                {product.stock_quantity > 0 
                                  ? `${product.stock_quantity} במלאי` 
                                  : 'אזל'
                                }
                              </span>
                              <span className="sm:hidden">
                                {product.stock_quantity > 0 
                                  ? `${product.stock_quantity}` 
                                  : 'אזל'
                                }
                              </span>
                            </div>
                          </div>
                          
                          {user?.role === 'company_admin' || user?.role === 'company_user' ? (
                            <div className="text-xs sm:text-sm text-gray-500 mt-1">
                              B2B: {formatPrice(product.price_b2b)}
                            </div>
                          ) : null}
                        </div>

                        {/* Add to Cart Button - Mobile Optimized */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            trackUserInteraction('add-to-cart', 'product-card');
                            addToCart(product, 1);
                          }}
                          disabled={product.stock_quantity === 0}
                          className={`w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-xs sm:text-sm touch-manipulation ${
                            product.stock_quantity === 0
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 hover:shadow-lg'
                          }`}
                        >
                          {product.stock_quantity === 0 ? 'אזל מהמלאי' : 'הוסף לעגלה'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

            {/* Pagination - Mobile Optimized */}
          {pagination.totalPages > 1 && (
          <div className="mt-6 sm:mt-8">
            {/* Mobile: Simple prev/next with page indicator */}
            <div className="flex sm:hidden justify-between items-center gap-2">
              <button
                onClick={() => setCurrentPage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation"
              >
                ← הקודם
              </button>
              
              <div className="flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg min-w-[80px]">
                {pagination.currentPage} / {pagination.totalPages}
              </div>
              
              <button
                onClick={() => setCurrentPage(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation"
              >
                הבא →
              </button>
            </div>

            {/* Desktop: Full pagination */}
            <div className="hidden sm:flex justify-center items-center gap-2">
              <button
                onClick={() => setCurrentPage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                הקודם
              </button>
              
              <div className="flex gap-1">
                {[...Array(Math.min(5, pagination.totalPages))].map((_, index) => {
                  const pageNum = Math.max(1, Math.min(
                    pagination.currentPage - 2 + index,
                    pagination.totalPages - 4 + index
                  ));
                  
                  if (pageNum > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                        pageNum === pagination.currentPage
                          ? 'bg-primary-600 text-white shadow-lg'
                          : 'border border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                הבא
              </button>
            </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      <MobileFilters
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        onFiltersChange={updateFilters}
        onApplyFilters={(newFilters) => {
          updateFilters(newFilters);
          setShowMobileFilters(false);
        }}
        categories={categories}
      />

      {/* Quick View Modal */}
      {quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          isOpen={!!quickViewProduct}
          onClose={closeQuickView}
          onAddToCart={addToCart}
          onToggleWishlist={toggleWishlist}
          isWishlisted={wishlist.has(quickViewProduct.id)}
          formatPrice={formatPrice}
          renderStars={renderStars}
          user={user}
        />
      )}

      {/* Perfume Finder Modal */}
      <PerfumeFinder
        isOpen={showPerfumeFinder}
        onClose={() => setShowPerfumeFinder(false)}
      />
    </div>
  );
};

export default ProductsPage;